/** i18n keys and English defaults for Ventora fan settings field labels and hints. */

import type { VentoraInMode } from 'src/types/ventora_fan';

export const VENTORA_IDLE_REFRESH_PRIORITY_HINT = {
	key: 'ventora_idle_refresh_priority_hint',
	default: 'Low priority. Active when no other trigger is enabled. Overridden by Light trigger.',
};

export const VENTORA_LIGHT_TRIGGER_PRIORITY_HINT = {
	key: 'ventora_light_trigger_priority_hint',
	default: 'Low priority. Active after Idle/Refresh. Overridden by Temperature trigger.',
};

export const VENTORA_TEMP_TRIGGER_PRIORITY_HINT = {
	key: 'ventora_temp_trigger_priority_hint',
	default: 'Normal priority. Active after Light trigger. Overridden by Humidity trigger.',
};

export const VENTORA_HUM_TRIGGER_PRIORITY_HINT = {
	key: 'ventora_hum_trigger_priority_hint',
	default: 'Normal priority. Active after Temperature trigger. Overridden by Night Mode.',
};

export const VENTORA_BOOST_DEFAULTS_PRIORITY_HINT = {
	key: 'ventora_boost_defaults_priority_hint',
	default: 'High priority. Active after Night Mode. Overrides all other triggers.',
};

export const VENTORA_IDLE_SPEED_HINT = {
	key: 'ventora_idle_speed_hint',
	default:
		'Slow rotation speed when idle to prevent air backflow. Set to 0 to turn the fan off when idle.',
};

export const VENTORA_REFRESH_SPEED_HINT = {
	key: 'ventora_refresh_speed_hint',
	default: 'Fan speed during periodic refresh.',
};

export const VENTORA_REFRESH_INTERVAL_HINT = {
	key: 'ventora_refresh_interval_hint',
	default: 'Time between refresh periods. The timer resets when any other trigger activates.',
};

export const VENTORA_REFRESH_DURATION_HINT = {
	key: 'ventora_refresh_duration_hint',
	default: 'How long the fan runs at refresh speed during each refresh period.',
};

export const VENTORA_LIGHT_SPEED_HINT = {
	key: 'ventora_light_speed_hint',
	default: 'Fan speed when the light trigger is active.',
};

export const VENTORA_LIGHT_ON_DELAY_HINT = {
	key: 'ventora_light_on_delay_hint',
	default:
		'Delay before starting the fan after lights turn on. Set to 0 for immediate start. Allows short visits without triggering the fan.',
};

export const VENTORA_LIGHT_OFF_DELAY_HINT = {
	key: 'ventora_light_off_delay_hint',
	default: 'Time the fan keeps running after lights turn off. Set to 0 to stop immediately.',
};

export const VENTORA_TEMP_SPEED_HINT = {
	key: 'ventora_temp_speed_hint',
	default: 'Fan speed when the temperature trigger is active.',
};

export const VENTORA_TEMP_THRESHOLD_HINT = {
	key: 'ventora_temp_threshold_hint',
	default:
		'Temperature threshold with ±1°C hysteresis. Fan activates above threshold +1°C, deactivates below threshold -1°C.',
};

export const VENTORA_TEMP_OFF_DELAY_HINT = {
	key: 'ventora_temp_off_delay_hint',
	default:
		'Time the fan keeps running after temperature drops below threshold. Set to 0 to stop immediately.',
};

export const VENTORA_HUM_SPEED_HINT = {
	key: 'ventora_hum_speed_hint',
	default: 'Fan speed when the humidity trigger is active.',
};

export const VENTORA_HUM_THRESHOLD_HINT = {
	key: 'ventora_hum_threshold_hint',
	default:
		'Humidity threshold with ±5% hysteresis. Fan activates above threshold +5%, deactivates below threshold -5%.',
};

export const VENTORA_HUM_OFF_DELAY_HINT = {
	key: 'ventora_hum_off_delay_hint',
	default:
		'Time the fan keeps running after humidity drops below threshold. Set to 0 to stop immediately.',
};

export const VENTORA_BOOST_SPEED_HINT = {
	key: 'ventora_boost_speed_hint',
	default: 'Fan speed during boost. Can be overridden per boost activation.',
};

export const VENTORA_BOOST_DURATION_HINT = {
	key: 'ventora_boost_duration_hint',
	default: 'How long the fan runs during boost. Can be overridden per boost activation.',
};

export const VENTORA_NIGHT_SPEED_LIMIT_HINT = {
	key: 'ventora_night_speed_limit_hint',
	default: 'Maximum fan speed while night mode is active.',
};

/** Input & Initial state (Ventora fan) — matches device web UI copy. */
export const VENTORA_IO_HEADING = {
	key: 'ventora_io_heading',
	default: 'Input & Initial state',
};

export const VENTORA_IO_SUBTITLE = {
	key: 'ventora_io_subtitle',
	default: 'Configure input behavior and power-on state.',
};

export const VENTORA_IO_SECTION_INITIAL = {
	key: 'ventora_io_section_initial_state',
	default: 'Initial state on power on',
};

export const VENTORA_IO_SECTION_INPUT_TYPE = {
	key: 'ventora_io_section_input_type',
	default: 'Input type',
};

export const VENTORA_IO_SECTION_INPUT_MODE = {
	key: 'ventora_io_section_input_mode',
	default: 'Input mode',
};

export const VENTORA_IO_INIT_DESC: Record<
	'off' | 'on' | 'restore_last',
	{ key: string; default: string }
> = {
	off: {
		key: 'ventora_io_init_off_desc',
		default: 'Fan stays off after power is applied until it is turned on.',
	},
	on: {
		key: 'ventora_io_init_on_desc',
		default: 'Fan turns on when power is applied.',
	},
	restore_last: {
		key: 'ventora_io_init_restore_desc',
		default: 'Fan restores the state it had before the device was powered off.',
	},
};

export const VENTORA_IO_INIT_PILL: Record<
	'off' | 'on' | 'restore_last',
	{ key: string; default: string }
> = {
	off: { key: 'ventora_io_pill_off', default: 'Off' },
	on: { key: 'ventora_io_pill_on', default: 'On' },
	restore_last: { key: 'ventora_io_pill_restore_last', default: 'Restore last' },
};

export const VENTORA_IO_INPUT_TYPE_DESC: Record<'button' | 'switch', { key: string; default: string }> =
	{
		switch: {
			key: 'ventora_io_input_type_switch_desc',
			default: 'A toggle switch that maintains its ON/OFF position.',
		},
		button: {
			key: 'ventora_io_input_type_button_desc',
			default: 'A push button that momentarily changes state.',
		},
	};

export const VENTORA_IO_IN_MODE_DESC: Record<VentoraInMode, { key: string; default: string }> = {
	detached: {
		key: 'ventora_fan_io_mode_detached_desc',
		default: 'Input is logically separated from fan control.',
	},
	follow: {
		key: 'ventora_fan_io_mode_follow_desc',
		default: 'Fan follows the state of the external input.',
	},
	auto: {
		key: 'ventora_fan_io_mode_auto_desc',
		default: 'Switch ON enables automatic mode, switch OFF turns the fan off.',
	},
	force: {
		key: 'ventora_fan_io_mode_force_desc',
		default: 'Forces fan behavior regardless of other inputs.',
	},
	boost: {
		key: 'ventora_fan_io_mode_boost_desc',
		default: 'Uses the input for boost activation.',
	},
	light: {
		key: 'ventora_fan_io_mode_light_desc',
		default: 'Uses the input as a light sensor instead of the built-in illuminance sensor.',
	},
};

export const VENTORA_IO_IN_MODE_PILL_LABEL: Record<VentoraInMode, { key: string; default: string }> = {
	detached: { key: 'ventora_in_mode_detached', default: 'Detached' },
	follow: { key: 'ventora_io_follow_input', default: 'Follow input' },
	auto: { key: 'ventora_in_mode_auto', default: 'Auto' },
	force: { key: 'ventora_in_mode_force', default: 'Force' },
	boost: { key: 'ventora_in_mode_boost', default: 'Boost' },
	light: { key: 'ventora_io_light_sense', default: 'Light sense' },
};
